import {
    AdditiveBlending,
    Color,
    InstancedMesh,
    Matrix4,
    Mesh,
    MeshBasicMaterial,
    Object3D,
    PlaneGeometry,
    ShaderMaterial,
    SphereGeometry,
    Vector3,
} from 'three';

import { map } from '@resn/gozer-math';
import { disposeObject } from '@resn/gozer-three/helpers';

import {
    shaderChunks as godrayChunks,
    uniforms as godrayUniforms,
} from '../../shaders/ShaderChunks/godray';
import { shaderChunks, uniforms } from '../../shaders/ShaderChunks/transitions/rip';
import ShineLayer from '../elements/shine/shine';

class RipTransition {
    static ripUniforms = uniforms;
    static uniforms = shaderChunks.uniforms;
    static vertex = shaderChunks.vertex;
    static fragment = shaderChunks.fragment;

    static MODES = {
        DEFAULT: 1,
        BURN: 0,
    };

    constructor(object, params = { mode: RipTransition.MODES.DEFAULT }) {
        this.object = object;

        this.name = 'rip';
        this.meshFilter = 'rip';
        this.transparent = true;
        this.mode = params.mode;

        this.duration = params.mode == RipTransition.MODES.DEFAULT ? 3 : 2.5;

        this.bottomMesh = object.getObjectByName('pack_bottom');
        this.topMesh = object.getObjectByName('pack_top');
        this.foilLine = object.getObjectByName('pack_foil_line');

        // this.bottomMesh.attach(this.topMesh);
        this.topMesh.frustumCulled = false;

        // Initialize sphere pool properties
        this.sphereData = [];
        this.gravity = 0.001;
    }

    setTransitionUniforms(shader) {
        shader.USE_TRANSITION_RIP = true;
        shader.USE_TRANSITION_BURN = this.mode == RipTransition.MODES.BURN;
        shader.TRANSITION_MODE = this.mode == RipTransition.MODES.BURN ? 0 : 1;
        // shader.needsUpdate = true;
    }

    createCustomMeshes(parent) {
        this.parent = parent;
        this.parent.visible = false;

        // Create geometry to be shared by all rays
        const planeGeo = new PlaneGeometry(2.5, 1);
        const material = new ShaderMaterial({
            vertexShader: godrayChunks.vertex,
            fragmentShader: godrayChunks.fragment,
            uniforms: {
                ...godrayUniforms(),
                u_time: { value: 0 },
                u_light_color: { value: new Color(0, 0.8, 1) },
                u_gamma_dist: { value: 20 },
                u_transition_progress: { value: 0 },
                u_ray_intensity: { value: 1 },
            },
            transparent: true,
            depthWrite: false,
            depthTest: false,
            blending: AdditiveBlending,
            name: 'RipTransitionShineMaterial',
        });
        const mesh = new Mesh(planeGeo, material);
        mesh.name = 'ShineMesh';
        mesh.position.set(0, 1, -0.1);
        // mesh.renderOrder = 1;

        this.shine = new Object3D();
        this.shine.add(mesh);
        this.shine.visible = this.mode == RipTransition.MODES.DEFAULT;

        this.shineMesh = mesh;

        parent.add(this.shine);

        this.shineLayer = new ShineLayer({
            lightColor: new Color(0.0, 0.5, 1),
        });
        this.shineLayer.position.z = 0.05;
        this.shineLayer.scale.set(0.3, 0.3, 1);
        this.shineLayer.material.transparent = true;
        this.shineLayer.material.blending = AdditiveBlending;
        this.shineLayer.material.depthWrite = false;
        this.shineLayer.material.depthTest = false;

        this.shineHighlightContainer = new Object3D();
        this.shineHighlightContainer.position.y = 0.54;
        this.shineHighlightContainer.scale.set(1, 0.5, 1);

        this.shineHighlightContainer.visible = this.mode == RipTransition.MODES.DEFAULT;
        // dont add shine layer, doesnt seem to add much
        // this.shineHighlightContainer.add(this.shineLayer);

        parent.add(this.shineHighlightContainer);

        // Create sphere pool
        const sphereGeometry = new SphereGeometry(0.003, 8, 8);
        const sphereMaterial = new MeshBasicMaterial({
            color: 0x00aaff,
            transparent: true,
            blending: AdditiveBlending,
            depthWrite: false,
            depthTest: false,
        });
        sphereMaterial.name = 'RipTransitionParticle';

        // Create instanced mesh for spheres
        const SPHERE_COUNT = 20;
        this.sphereInstancedMesh = new InstancedMesh(sphereGeometry, sphereMaterial, SPHERE_COUNT);
        this.sphereInstancedMesh.name = 'RipTransitionParticles';
        this.sphereInstancedMesh.renderOrder = 5;
        this.sphereInstancedMesh.instanceMatrix.setUsage(35044); // THREE.DynamicDrawUsage
        this.sphereInstancedMesh.count = 0; // Start with 0 visible instances
        this.sphereInstancedMesh.frustumCulled = false; // Ensure it's always rendered
        this.sphereInstancedMesh.position.z = -0.03;

        // Initialize all matrices to identity
        const identityMatrix = new Matrix4();
        for (let i = 0; i < SPHERE_COUNT; i++) {
            this.sphereInstancedMesh.setMatrixAt(i, identityMatrix);
        }
        this.sphereInstancedMesh.instanceMatrix.needsUpdate = true;

        parent.add(this.sphereInstancedMesh);

        // Initialize matrices and sphere data
        this.sphereMatrices = [];
        this.sphereData = [];

        for (let i = 0; i < SPHERE_COUNT; i++) {
            this.sphereMatrices.push(new Matrix4().identity());
            this.sphereData.push({
                velocity: new Vector3(0, 0, 0),
                lifeTime: 0,
                active: false,
                scale: 0,
                position: new Vector3(0, 0, 0),
                rotation: { z: 0 },
            });
        }
    }

    destroy() {
        this.reset();

        disposeObject(this.parent);

        // Clean up shine highlight
        if (this.shineLayer) {
            this.shineLayer.material?.dispose();
            this.shineHighlightContainer?.remove(this.shineLayer);
        }
        this.shineHighlightContainer = null;
        this.shineLayer = null;

        // Clean up shine mesh
        if (this.shineMesh) {
            this.shineMesh?.material.dispose();
            this.shineMesh?.geometry.dispose();
            this.shine.remove(this.shineMesh);
        }
        this.shine = null;
        this.shineMesh = null;

        // Clean up instanced mesh
        if (this.sphereInstancedMesh) {
            this.sphereInstancedMesh.parent?.remove(this.sphereInstancedMesh);
            this.sphereInstancedMesh.geometry?.dispose();
            this.sphereInstancedMesh.material?.dispose();
            this.sphereInstancedMesh.instanceMatrix.array = null;
            this.sphereInstancedMesh = null;
        }

        // Clear arrays
        if (this.sphereMatrices) this.sphereMatrices.length = 0;
        this.sphereMatrices = null;

        if (this.sphereData) this.sphereData.length = 0;
        this.sphereData = null;
    }

    updateParticles() {
        const highlightWorldPos = this.shineLayer.position
            .clone()
            .add(this.shineHighlightContainer.position);

        for (let i = 0; i < this.sphereData.length; i++) {
            const data = this.sphereData[i];

            if (data.active) {
                // Update position
                data.position.add(data.velocity);

                // Apply gravity
                data.velocity.y -= this.gravity;

                // Update scale with smooth in/out
                const normalizedLife = (data.lifeTime + 1) / 20;
                // const scaleValue = Math.sin(normalizedLife * Math.PI) * 0.5 + 0.5;
                const s = 1 - normalizedLife;

                // Create transformation matrix
                const matrix = this.sphereMatrices[i];

                // Calculate rotation to align with velocity
                const angle = Math.atan2(data.velocity.y, data.velocity.x) - Math.PI * 0.5;
                data.rotation.z = angle;

                // Reset matrix
                matrix.identity();

                // First apply rotation
                matrix.makeRotationZ(data.rotation.z);

                // Then apply scale
                const scaleVec = new Vector3(s * 0.5, s * 6, s);
                matrix.scale(scaleVec);

                // Finally apply position
                matrix.setPosition(data.position.x, data.position.y, data.position.z);

                // Apply matrix to instance
                this.sphereInstancedMesh.setMatrixAt(i, matrix);

                data.lifeTime++;

                // Reset if lifetime exceeded
                if (data.lifeTime >= 20) data.active = false;
            }
        }

        // Spawn new spheres when progress < 0.2
        if (this.progress < 0.3) {
            // Find an inactive sphere
            const inactiveSphereIndex = this.sphereData.findIndex((data) => !data.active);
            if (inactiveSphereIndex !== -1) {
                const data = this.sphereData[inactiveSphereIndex];

                // Reset and activate
                data.position.set(highlightWorldPos.x, highlightWorldPos.y, highlightWorldPos.z);
                data.velocity.set(
                    (Math.random() - 1) * 0.02,
                    (Math.random() - 0.5) * -0.02,
                    (Math.random() - 0.5) * 0.02
                );
                data.lifeTime = 0;
                data.active = true;

                // Initialize matrix for this instance
                const matrix = this.sphereMatrices[inactiveSphereIndex];
                matrix.identity();

                // Start with a small but visible scale, not zero
                const initialScale = new Vector3(0.1, 0.1, 0.1);
                matrix.scale(initialScale);

                matrix.setPosition(data.position.x, data.position.y, data.position.z);
                this.sphereInstancedMesh.setMatrixAt(inactiveSphereIndex, matrix);
            }
        }

        // Update instance count and flag instanceMatrix for update
        let activeCount = 0;
        for (let i = 0; i < this.sphereData.length; i++) {
            if (this.sphereData[i].active) {
                activeCount++;
            }
        }

        this.sphereInstancedMesh.count = activeCount;
        this.sphereInstancedMesh.instanceMatrix.needsUpdate = true;
    }

    update(progress) {
        this.shineMesh.material.uniforms.u_time.value = performance.now() / 1000;
        this.shineLayer.material.uniforms.uTime.value =
            performance.now() / 100 + this.progress * 50;
        this.updateParticles();
        // Update spheres
    }

    setProgress(progress) {
        this.progress = progress;
        // this.parent.visible = progress > 0;
        this.parent.visible = false;

        const easePower = 2.0;
        const animationStart = 0.25;
        const animationProgress =
            Math.pow(Math.max(0, progress - animationStart), easePower) /
            Math.pow(1 - animationStart, easePower);

        if (this.mode == RipTransition.MODES.DEFAULT) {
            this.object.position.y = animationProgress * -4;
        }

        this.shineMesh.material.uniforms.u_transition_progress.value = progress;

        const moveRange = 0.35;
        this.shineLayer.position.x = map(progress, 0, 0.3, -moveRange, moveRange, true);
        // this.shineLayer.position.x =
        //     Math.pow(Math.min(1, progress * 4), 2.0) * (moveRange * 2) - moveRange;
        const s = Math.pow(Math.sin(Math.min(1, progress * 4) * Math.PI), 0.2) * 0.25;
        // this.shineLayer.position.x =
        //     Math.pow(Math.min(1, progress * 4), 2.0) * (moveRange * 2) - moveRange;
        // const s = Math.pow(Math.sin(Math.min(1, progress * 4) * Math.PI), 0.2) * 0.25;

        this.shineLayer.scale.set(s, 0.5 * s, 1);
        this.shineLayer.material.uniforms.uLightIntensity.value = Math.max(
            0.0,
            Math.min(1, Math.pow(Math.sin(Math.min(1, progress * 4) * Math.PI), 0.25))
        );

        this.preProgress = progress;
    }

    reset() {
        this.object.position.set(0, 0, 0);
        this.object.rotation.set(0, 0, 0);

        this.shineLayer.position.x = -0.35;
        this.shineLayer.scale.set(0, 0, 1);
        this.shineLayer.material.uniforms.uLightIntensity.value = 0;

        this.sphereInstancedMesh.count = 0;
        this.sphereInstancedMesh.instanceMatrix.needsUpdate = true;

        for (let i = 0; i < this.sphereData.length; i++) {
            this.sphereData[i].active = false;
            this.sphereData[i].lifeTime = 0;
            this.sphereData[i].position.set(0, 0, 0);
            this.sphereData[i].velocity.set(0, 0, 0);
            this.sphereData[i].rotation.z = 0;
            this.sphereData[i].scale = 0;
        }

        this.progress = 0;
        this.preProgress = 0;
    }
}
export { RipTransition };
